<template>
  <div class="alarm-category-chart-wrapper">
    <div ref="chartDom" class="chart-container"></div>

    <!-- 全屏对话框 -->
    <v-dialog v-model="isFullscreen" :width="dialogWidth" :height="dialogHeight" persistent class="fullscreen-dialog">
      <v-card class="fullscreen-card">
        <!-- 对话框标题栏 -->
        <div class="fullscreen-header">
          <div class="fullscreen-title">告警类别统计</div>
          <v-icon icon="mdi-close" class="close-icon" @click="closeFullscreen"></v-icon>
        </div>

        <!-- 放大的内容区域 -->
        <div class="fullscreen-body">
          <AlarmCategoryChartInner :data="data" class="fullscreen-chart-container" />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, computed, defineComponent, h } from 'vue'
import { initChart } from '@/utils'
import getAlarmCategoryChartOption from '@/views/operation/config/alarmCategoryConfig'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({ categories: [], data: [] })
  }
})

const chartDom = ref(null)
let chartObj = null
let resizeObserver = null

// 全屏状态
const isFullscreen = ref(false)

// 窗口尺寸
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1920)
const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 1080)

const updateWindowSize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
    windowHeight.value = window.innerHeight
  }
}

// 计算对话框宽度
const dialogWidth = computed(() => {
  const maxWidth = Math.min(windowWidth.value * 0.9, 1400)
  return Math.floor(maxWidth)
})

// 计算对话框高度
const dialogHeight = computed(() => {
  const maxHeight = Math.min(windowHeight.value * 0.9, 900)
  return Math.floor(maxHeight)
})

const chartOption = computed(() => {
  const { categories, data } = props.data
  return getAlarmCategoryChartOption(categories, data)
})

const updateChart = () => {
  if (!chartObj) return
  chartObj.setOption(chartOption.value)
}

const initChartInstance = () => {
  if (!chartDom.value) return
  chartObj = initChart(chartDom.value)
  updateChart()

  // 监听容器大小变化
  resizeObserver = new ResizeObserver(() => {
    chartObj?.resize()
  })
  if (chartDom.value) {
    resizeObserver.observe(chartDom.value)
  }
}

// 内部图表组件
const AlarmCategoryChartInner = defineComponent({
  props: {
    data: {
      type: Object,
      default: () => ({ categories: [], data: [] })
    }
  },
  setup(props) {
    const chartDom = ref(null)
    let chartObj = null
    let resizeObserver = null

    const chartOption = computed(() => {
      const { categories, data } = props.data
      return getAlarmCategoryChartOption(categories, data)
    })

    const initChartInstance = () => {
      if (!chartDom.value) return
      chartObj = initChart(chartDom.value)
      chartObj.setOption(chartOption.value)

      resizeObserver = new ResizeObserver(() => {
        chartObj?.resize()
      })
      if (chartDom.value) {
        resizeObserver.observe(chartDom.value)
      }
    }

    watch(() => props.data, () => {
      if (chartObj) {
        chartObj.setOption(chartOption.value)
      }
    }, { deep: true })

    watch(() => chartOption.value, () => {
      if (chartObj) {
        chartObj.setOption(chartOption.value)
      }
    })

    onMounted(() => {
      nextTick(() => {
        initChartInstance()
      })
    })

    onUnmounted(() => {
      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }
      if (chartObj) {
        chartObj.dispose()
        chartObj = null
      }
    })

    return () => h('div', { ref: chartDom, class: 'chart-container' })
  }
})

// 打开全屏（暴露给父组件调用）
const openFullscreen = () => {
  isFullscreen.value = true
}

// 暴露方法给父组件
defineExpose({
  openFullscreen
})

// 关闭全屏
const closeFullscreen = () => {
  isFullscreen.value = false
}

watch(() => chartOption.value, () => {
  updateChart()
}, { deep: true })

watch(() => props.data, () => {
  updateChart()
}, { deep: true })

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateWindowSize)
    updateWindowSize()
  }
  nextTick(() => {
    initChartInstance()
  })
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateWindowSize)
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (chartObj) {
    chartObj.dispose()
    chartObj = null
  }
})
</script>

<style scoped lang="scss">
.alarm-category-chart-wrapper {
  width: 100%;
  height: 100%;
  max-height: 400px;
}

.chart-container {
  width: 100%;
  height: 100%;
}

// 全屏对话框样式
.fullscreen-dialog {
  :deep(.v-overlay__content) {
    max-width: none !important;
    max-height: none !important;
  }
}

.fullscreen-card {
  border-radius: 30px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(255, 255, 255, 1);
}

.fullscreen-header {
  position: relative;
  height: 60px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
  z-index: 10;
}

.fullscreen-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: 500;
  line-height: normal;
}

.close-icon {
  cursor: pointer;
  transition: transform 0.2s ease;
  color: #000;

  &:hover {
    transform: scale(1.1);
  }
}

.fullscreen-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
  padding: 20px;
}

.fullscreen-chart-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}
</style>
